/**
 * Vista pública de stream (§12) — lo primero que ve cualquiera al entrar al sitio.
 *
 * SIN LOGIN. Tiene dos modos, elegibles desde un selector arriba:
 *   · `compact` — bandas angostas: ocupa poco alto, cómodo para lectura rápida.
 *   · `full`    — el diseño de pantalla grande (el clásico /hud), sin sidebar.
 *
 * No muestra datos personales: los nombres de los boletos llegan enmascarados de la API.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FightMetrics, FightStatus, PublicTicket, StreamEvent } from '@thepit/contracts';
import { centsToWire, mulDivFloor } from '@thepit/contracts';
import { usePublicFight, usePublicTickets } from '../lib/queries.js';
import { usePublicStream, type StreamStatus } from '../lib/usePublicStream.js';
import { getSetting, setSetting } from '../lib/db.js';
import { useT } from '../lib/i18n.js';
import { formatMoney } from '../lib/format.js';
import { CornerBand, PoolPill, TensionBar, Ticker } from './stream-parts.js';

/** Modos de vista del stream. Se guarda por dispositivo. */
type Mode = 'compact' | 'full';

const MODES: readonly { id: Mode; key: string }[] = [
  { id: 'compact', key: 'stream.modeCompact' },
  { id: 'full', key: 'stream.modeFull' },
];

const STATUS_KEY: Record<FightStatus, string> = {
  open: 'obs.open',
  locked: 'obs.locked',
  settled: 'obs.settled',
  cancelled: 'fight.status.cancelled',
};

export function StreamView({ forceMode }: { forceMode?: Mode }): JSX.Element {
  const t = useT();
  const { data, isLoading, refetch } = usePublicFight();
  const ticketsQuery = usePublicTickets();
  const fight = data?.fight ?? null;

  const [modeChoice, setMode] = useState<Mode>('compact');
  const [metrics, setMetrics] = useState<FightMetrics | null>(null);
  const [tickets, setTickets] = useState<PublicTicket[]>([]);
  const [status, setStatus] = useState<StreamStatus>('connecting');

  // `/hud` es un acceso directo al modo pantalla completa: no muestra el selector.
  const mode = forceMode ?? modeChoice;

  useEffect(() => {
    void (async () => {
      const saved = await getSetting('stream.mode');
      if (saved === 'compact' || saved === 'full') setMode(saved);
    })();
  }, []);

  useEffect(() => {
    void setSetting('stream.mode', mode);
  }, [mode]);

  useEffect(() => {
    if (data?.metrics) setMetrics(data.metrics);
  }, [data?.metrics]);

  useEffect(() => {
    if (ticketsQuery.data?.items) setTickets(ticketsQuery.data.items);
  }, [ticketsQuery.data]);

  const onEvent = useCallback((event: StreamEvent) => {
    if (event.type === 'pool.updated') setMetrics(event.metrics);
    if (event.type === 'ticket.created') {
      // Dedup por recibo: el mismo boleto puede llegar por SSE y por el refetch REST.
      setTickets((prev) =>
        [event.ticket, ...prev.filter((t) => t.receiptCode !== event.ticket.receiptCode)].slice(0, 10),
      );
    }
  }, []);

  const onPoll = useCallback(() => {
    void refetch();
    void ticketsQuery.refetch();
  }, [refetch, ticketsQuery]);

  const { status: liveStatus } = usePublicStream({ onEvent, onPoll, enabled: Boolean(fight) });

  useEffect(() => setStatus(liveStatus), [liveStatus]);

  if (isLoading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <div className="pit-skeleton h-32 w-full max-w-3xl rounded-[var(--pit-radius-xl)]" role="status" />
      </div>
    );
  }

  if (!fight) {
    return (
      <div className="pit-arena pit-anim-up relative flex min-h-[80vh] flex-col items-center justify-center gap-5 overflow-hidden rounded-[var(--pit-radius-xl)] px-4 text-center">
        <span
          aria-hidden
          className="pit-hex pit-anim-float grid h-24 w-24 place-items-center"
          style={{
            background:
              'linear-gradient(160deg, color-mix(in srgb, var(--pit-accent-red) 60%, transparent), color-mix(in srgb, var(--pit-accent-amber) 25%, transparent))',
            boxShadow: '0 0 50px -10px var(--pit-accent-red)',
          }}
        >
          <span className="pit-mono text-[var(--pit-text-2xl)] font-[var(--pit-weight-black)] text-white">TP</span>
        </span>
        <p className="pit-title pit-mono text-[clamp(1.5rem,5vw,3.25rem)] font-[var(--pit-weight-black)] uppercase tracking-[var(--pit-tracking-widest)]">
          {t('fight.none')}
        </p>
        <p className="max-w-md text-[var(--pit-text-sm)] text-[var(--pit-text-muted)]">{t('fight.noneHint')}</p>
        <span className="pit-label">{t('app.name')} · {t('app.tagline')}</span>
      </div>
    );
  }

  const m = metrics ?? fight.metrics;
  const currency = fight.rulesSnapshot.currency;
  let redPct = 50;
  if (m.totalPoolCents > 0) {
    redPct = centsToWire(mulDivFloor(BigInt(m.redPoolCents), 100n, BigInt(m.totalPoolCents)));
  }

  const redFav = m.redPoolCents > m.bluePoolCents;
  const blueFav = m.bluePoolCents > m.redPoolCents;
  const statusLabel = t(STATUS_KEY[fight.status] ?? 'obs.open');

  return (
    <div className="pit-arena pit-anim-up flex flex-col gap-3 rounded-[var(--pit-radius-xl)] p-3 lg:p-5">
      {/* Cabecera: marca · pelea · selector de modo · estado */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex flex-col">
          <span className="pit-title pit-mono text-[var(--pit-text-lg)] font-[var(--pit-weight-black)] uppercase tracking-[var(--pit-tracking-widest)]">
            {t('app.name')}
          </span>
          <span className="pit-label hidden sm:block">{t('app.tagline')}</span>
        </div>
        {mode === 'compact' && (
          <span className="hidden max-w-[40ch] truncate text-[var(--pit-text-sm)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-secondary)] lg:inline">
            {fight.title}
          </span>
        )}

        {/* Selector de modo de vista (oculto cuando la ruta fuerza un modo) */}
        {!forceMode && (
          <div
            role="tablist"
            aria-label={t('stream.modeLabel')}
            className="pit-glass ml-auto flex gap-1 rounded-[var(--pit-radius-md)] p-0.5"
          >
            {MODES.map((item) => {
              const active = mode === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMode(item.id)}
                  className={[
                    'pit-touch rounded-[var(--pit-radius-sm)] px-2.5 py-1',
                    'pit-mono text-[var(--pit-text-3xs)] uppercase tracking-[var(--pit-tracking-wider)]',
                    'transition-colors duration-[var(--pit-duration-fast)]',
                    active
                      ? 'bg-[var(--pit-bg-overlay)] text-[var(--pit-text-primary)]'
                      : 'text-[var(--pit-text-muted)] hover:text-[var(--pit-text-secondary)]',
                  ].join(' ')}
                >
                  {t(item.key)}
                </button>
              );
            })}
          </div>
        )}

        <span className={forceMode ? 'ml-auto flex items-center gap-2' : 'flex items-center gap-2'}>
          <span className={`pit-live-dot ${status === 'live' ? '' : 'pit-live-dot--off'}`} aria-hidden />
          <span className="pit-mono text-[var(--pit-text-3xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-secondary)]">
            {t(`stream.${status}`)}
          </span>
        </span>
      </header>

      {mode === 'compact' ? (
        <>
          <div className="grid gap-2 lg:grid-cols-[1fr_auto_1fr]">
            <CornerBand
              which="red"
              name={fight.redFighter.name}
              crew={fight.redFighter.crew}
              record={fight.redFighter.record}
              poolCents={m.redPoolCents}
              oddsBp={m.oddsRedBp}
              betCount={m.redBetCount}
              currency={currency}
              isFavorite={redFav}
            />
            <PoolPill
              totalCents={m.totalPoolCents}
              rakeBp={fight.rulesSnapshot.rakeBp}
              currency={currency}
              statusLabel={statusLabel}
            />
            <CornerBand
              which="blue"
              name={fight.blueFighter.name}
              crew={fight.blueFighter.crew}
              record={fight.blueFighter.record}
              poolCents={m.bluePoolCents}
              oddsBp={m.oddsBlueBp}
              betCount={m.blueBetCount}
              currency={currency}
              isFavorite={blueFav}
            />
          </div>
          <TensionBar redPct={redPct} />
          <Ticker tickets={tickets} currency={currency} />
        </>
      ) : (
        <FullStage
          title={fight.title}
          statusLabel={statusLabel}
          redName={fight.redFighter.name}
          redCrew={fight.redFighter.crew}
          redRecord={fight.redFighter.record}
          blueName={fight.blueFighter.name}
          blueCrew={fight.blueFighter.crew}
          blueRecord={fight.blueFighter.record}
          m={m}
          rakeBp={fight.rulesSnapshot.rakeBp}
          currency={currency}
          redPct={redPct}
          redFav={redFav}
          blueFav={blueFav}
          tickets={tickets}
        />
      )}
    </div>
  );
}

/** Modo pantalla completa: el diseño grande, sin sidebar. */
function FullStage({
  title,
  statusLabel,
  redName,
  redCrew,
  redRecord,
  blueName,
  blueCrew,
  blueRecord,
  m,
  rakeBp,
  currency,
  redPct,
  redFav,
  blueFav,
  tickets,
}: {
  title: string;
  statusLabel: string;
  redName: string;
  redCrew: string | null;
  redRecord: { wins: number; losses: number; draws: number } | null;
  blueName: string;
  blueCrew: string | null;
  blueRecord: { wins: number; losses: number; draws: number } | null;
  m: FightMetrics;
  rakeBp: number;
  currency: { code: string; symbol: string; decimals: number };
  redPct: number;
  redFav: boolean;
  blueFav: boolean;
  tickets: PublicTicket[];
}): JSX.Element {
  const t = useT();
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <div className="flex flex-col items-center gap-1 text-center">
        <p className="pit-title pit-mono text-[clamp(1.1rem,2.6vw,2.2rem)] font-[var(--pit-weight-black)] uppercase tracking-[clamp(0.1em,0.5vw,0.35em)]">
          {title}
        </p>
      </div>
      <div className="grid items-stretch gap-3 lg:grid-cols-[1fr_auto_1fr]">
        <CornerBand
          which="red"
          name={redName}
          crew={redCrew}
          record={redRecord}
          poolCents={m.redPoolCents}
          oddsBp={m.oddsRedBp}
          betCount={m.redBetCount}
          currency={currency}
          isFavorite={redFav}
          size="lg"
        />
        <div className="flex flex-col items-center justify-center gap-2">
          <span
            aria-hidden
            className="pit-mono text-[clamp(1.6rem,4vw,3rem)] font-[var(--pit-weight-black)] italic text-[var(--pit-text-muted)]"
            style={{ textShadow: '0 0 30px rgba(255,255,255,0.15)' }}
          >
            {t('obs.vs')}
          </span>
          <PoolPill
            totalCents={m.totalPoolCents}
            rakeBp={rakeBp}
            currency={currency}
            size="lg"
            statusLabel={statusLabel}
          />
        </div>
        <CornerBand
          which="blue"
          name={blueName}
          crew={blueCrew}
          record={blueRecord}
          poolCents={m.bluePoolCents}
          oddsBp={m.oddsBlueBp}
          betCount={m.blueBetCount}
          currency={currency}
          isFavorite={blueFav}
          size="lg"
        />
      </div>
      <TensionBar redPct={redPct} />
      <Ticker tickets={tickets} currency={currency} rows={10} title={t('obs.recentBets')} />
    </section>
  );
}
